import { Link } from "react-router-dom";
import { SEOHead } from "@/components/SEOHead";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Check, X } from "lucide-react";
import { glampingProductSchema } from "@/lib/schema";
import { GLAMPING_TECH_SPECS } from "@/content/amenities";
import heroImage from "@/assets/glamping/tent-cabana-daytime.webp";

const competitors = [
  { key: "sons", label: "Son's Guadalupe", highlight: true },
  { key: "mystic", label: "Mystic Quarry" },
  { key: "geronimo_creek", label: "Geronimo Creek Retreat" },
  { key: "fimfo", label: "Camp Fimfo Glamping" },
];

type Cell = { v: string; ok?: boolean };

const rows: { feature: string; cells: Record<string, Cell> }[] = [
  { feature: "Tent type", cells: {
    sons: { v: "16-ft canvas bell tent", ok: true },
    mystic: { v: "Cabin / safari tent", ok: true },
    geronimo_creek: { v: "Cabin", ok: true },
    fimfo: { v: "Glamping cabin", ok: true },
  }},
  { feature: "Air conditioning", cells: {
    sons: { v: "Stand-up AC + 2 high-power fans", ok: true },
    mystic: { v: "Yes (cabin units)", ok: true },
    geronimo_creek: { v: "Yes", ok: true },
    fimfo: { v: "Yes", ok: true },
  }},
  { feature: "Real beds", cells: {
    sons: { v: "2 queen foam + 1 twin (sleeps 5)", ok: true },
    mystic: { v: "Real beds", ok: true },
    geronimo_creek: { v: "Real beds", ok: true },
    fimfo: { v: "Real beds", ok: true },
  }},
  { feature: "Direct river access", cells: {
    sons: { v: "Steps from the Guadalupe", ok: true },
    mystic: { v: "Cibolo Creek nearby", ok: true },
    geronimo_creek: { v: "Geronimo Creek frontage", ok: true },
    fimfo: { v: "Short walk", ok: true },
  }},
  { feature: "Float ends at your spot", cells: {
    sons: { v: "Yes — float to your basecamp", ok: true },
    mystic: { v: "No", ok: false },
    geronimo_creek: { v: "No", ok: false },
    fimfo: { v: "No", ok: false },
  }},
  { feature: "Private fire pit", cells: {
    sons: { v: "Yes — every site", ok: true },
    mystic: { v: "Some sites", ok: true },
    geronimo_creek: { v: "Yes", ok: true },
    fimfo: { v: "Yes", ok: true },
  }},
  { feature: "In-water covered picnic tables", cells: {
    sons: { v: "Yes — unique to Son's", ok: true },
    mystic: { v: "No", ok: false },
    geronimo_creek: { v: "No", ok: false },
    fimfo: { v: "No", ok: false },
  }},
  { feature: "Starting price / night", cells: {
    sons: { v: "From $99", ok: true },
    mystic: { v: "From ~$150", ok: true },
    geronimo_creek: { v: "From ~$199", ok: true },
    fimfo: { v: "From ~$179", ok: true },
  }},
];

const LuxuryGlampingNewBraunfels = () => {
  return (
    <div className="min-h-screen">
      <SEOHead
        title="Luxury Glamping Near New Braunfels, Texas"
        description="Luxury glamping near New Braunfels: AC-cooled bell tents with real beds on the Guadalupe River from $99. Compare Son's, Mystic Quarry & Geronimo Creek."
        canonical="/luxury-glamping-new-braunfels"
        ogImage="/og/glamping.jpg"
        schemaJson={[
          glampingProductSchema(
            "https://sonsguadalupe.com/luxury-glamping-new-braunfels",
            "https://sonsguadalupe.com/og/glamping.jpg",
          ),
        ]}
      />
      <Header />
      <main className="pt-24">
        <section className="relative h-[50vh] min-h-[380px]">
          <img fetchPriority="high" loading="eager" decoding="async" src={heroImage} alt="Luxury AC-cooled bell tent glamping on the Guadalupe River near New Braunfels" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-charcoal/80 via-charcoal/50 to-transparent" />
          <div className="absolute inset-0 flex items-center">
            <div className="container-narrow mx-auto px-4 md:px-8">
              <div className="max-w-3xl">
                <p className="text-coral-orange font-medium mb-3">Luxury glamping vs. camping</p>
                <h1 className="font-heading text-4xl md:text-6xl font-bold text-white mb-4">
                  Luxury Glamping Near New Braunfels
                </h1>
                <p className="text-white/90 text-lg md:text-xl max-w-2xl">
                  AC-cooled bell tents with real beds on the Guadalupe River — from $99/night. Here's how Son's stacks up against the other luxury glamping operators in the Hill Country.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="section-padding bg-background">
          <div className="container-narrow mx-auto max-w-3xl px-4 md:px-8">
            <h2 className="font-heading text-3xl font-bold text-foreground mb-4">This is glamping, not camping</h2>
            <p className="text-muted-foreground text-lg leading-relaxed mb-4">
              No sleeping bags, no air mattresses, no roughing it. Son's Guadalupe luxury glamping bell tents come with stand-up air conditioning, two high-power fans, and two queen foam mattresses plus a twin — sleeps five comfortably with full electricity, a lantern, and a private fire pit.
            </p>
            <p className="text-muted-foreground text-lg leading-relaxed">
              Showers and restrooms are in a clean, climate-controlled bathhouse a short walk away. The Guadalupe River is steps from your tent. And during the day you have full access to a private cabana with a charcoal BBQ pit and picnic table.
            </p>
          </div>
        </section>

        <section className="section-padding bg-cream">
          <div className="container-narrow mx-auto px-4 md:px-8">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-charcoal mb-8 text-center">
              Bell tent technical specs
            </h2>
            <div className="bg-white rounded-2xl shadow-soft overflow-hidden max-w-3xl mx-auto">
              <table className="w-full">
                <tbody>
                  {GLAMPING_TECH_SPECS.map((spec) => (
                    <tr key={spec.name} className="border-b border-cream last:border-0">
                      <td className="p-4 font-semibold text-charcoal align-top w-1/3">{spec.name}</td>
                      <td className="p-4 text-charcoal/80">{spec.value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        <section className="section-padding bg-background">
          <div className="container-narrow mx-auto px-4 md:px-8">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-8 text-center">
              Luxury glamping comparison — Hill Country
            </h2>
            <div className="overflow-x-auto bg-white rounded-2xl shadow-soft">
              <table className="w-full text-sm md:text-base">
                <thead>
                  <tr className="bg-teal text-white">
                    <th className="text-left p-4 font-heading">Feature</th>
                    {competitors.map((c) => (
                      <th key={c.key} className={`text-left p-4 font-heading min-w-[160px] ${c.highlight ? "bg-coral-orange" : ""}`}>{c.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.feature} className="border-t border-cream">
                      <td className="p-4 font-semibold text-charcoal align-top">{row.feature}</td>
                      {competitors.map((c) => {
                        const cell = row.cells[c.key];
                        return (
                          <td key={c.key} className={`p-4 align-top ${c.highlight ? "bg-cream/40" : ""}`}>
                            <div className="flex items-start gap-2">
                              {cell.ok ? <Check className="w-4 h-4 text-teal flex-shrink-0 mt-1" /> : <X className="w-4 h-4 text-red-500 flex-shrink-0 mt-1" />}
                              <span className="text-charcoal/80">{cell.v}</span>
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        <section className="section-padding bg-teal text-white">
          <div className="container-narrow mx-auto px-4 md:px-8 text-center max-w-2xl">
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">Sleep beside the Guadalupe — from $99/night</h2>
            <Link to="/glamping"><Button variant="hero" size="xl">Book a Bell Tent</Button></Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default LuxuryGlampingNewBraunfels;
